import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeSlash, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const BG = "/lottie-login-bg.jpg";

export default function SetPassword() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [reason, setReason] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); setValid(false); setReason("missing"); return; }
    api.get(`/auth/set-password/validate?token=${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setValid(!!data.valid);
        setReason(data.reason || "");
        if (data.email) { setEmail(data.email); setResendEmail(data.email); }
      })
      .catch(() => { setValid(false); setReason("error"); })
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 12 || !/\d/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
      setError("Password must be at least 12 characters and include at least one number and one special character");
      return;
    }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password });
      login(data.user, data.access_token);
      toast.success("Password set — welcome to WorkWithLottie!");
      nav("/app");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    try {
      await api.post("/auth/forgot-password", { email: resendEmail.trim() });
      setResent(true);
      toast.success("If that email has an account, a new link is on its way.");
    } catch {
      setResent(true);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 bg-navy relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="bg-white rounded-2xl p-5 shadow-xl w-fit">
            <img src="/lottie-logo.png" alt="Lottie" className="h-36 w-auto object-contain" />
          </div>
          <div>
            <h1 className="font-heading font-black text-5xl leading-[1.05] tracking-tighter">
              You're moments away from automated month-ends
            </h1>
            <p className="text-white/70 mt-5 max-w-md text-base">
              Set a password and we'll take you straight in.
            </p>
          </div>
          <div className="overline" style={{ color: "rgba(242,111,33,0.85)" }}>Set your password</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 bg-appbg">
        <div className="w-full max-w-md fade-up">
          <img src="/lottie-logo.png" alt="Lottie" className="lg:hidden h-24 w-auto object-contain mb-6" />

          {checking ? (
            <div className="text-navy/70 text-sm" data-testid="setpw-checking">Checking your link…</div>
          ) : valid ? (
            <>
              <div className="overline mb-2" style={{ color: "#F37A24" }}>Set your password</div>
              <h2 className="font-heading font-bold text-3xl tracking-tighter text-navy mb-2">Choose a password</h2>
              {email && <p className="text-navy/70 text-sm mb-6">for <b className="text-navy">{email}</b></p>}
              <form onSubmit={submit} className="space-y-4" data-testid="setpw-form">
                <label className="block">
                  <span className="overline text-navy/70 block mb-1.5">New password</span>
                  <div className="relative">
                    <input data-testid="setpw-password" type={show ? "text" : "password"} required value={password}
                      onChange={(e) => setPassword(e.target.value)} className="acc-input pr-10" placeholder="At least 12 characters" />
                    <button type="button" onClick={() => setShow(!show)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/70 hover:text-navy">
                      {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <span className="text-navy/65 text-xs mt-1.5 block">Min 12 characters, with at least 1 number and 1 special character.</span>
                </label>
                <label className="block">
                  <span className="overline text-navy/70 block mb-1.5">Confirm password</span>
                  <input data-testid="setpw-confirm" type={show ? "text" : "password"} required value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} className="acc-input" placeholder="Re-enter password" />
                </label>
                {error && <div data-testid="setpw-error" className="text-negative text-sm font-medium">{error}</div>}
                <button type="submit" disabled={loading} data-testid="setpw-submit"
                  className="lottie-btn w-full text-white font-semibold py-3 rounded-xl transition-colors duration-200 disabled:opacity-60">
                  {loading ? "PLEASE WAIT…" : "Set password & sign in"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="setpw-invalid">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(229,72,77,0.12)" }}>
                <WarningCircle size={26} weight="fill" className="text-negative" />
              </div>
              <h2 className="font-heading font-bold text-2xl tracking-tighter text-navy mb-2">
                {reason === "expired" ? "This link has expired" : "This link isn't valid"}
              </h2>
              <p className="text-navy/70 text-sm mb-6">
                No problem — enter your email and we'll send you a fresh link to set your password and sign in.
              </p>
              {resent ? (
                <div className="flex items-center gap-2 text-positive text-sm font-medium" data-testid="setpw-resent">
                  <CheckCircle size={18} weight="fill" /> If that email has an account, a new link is on its way.
                </div>
              ) : (
                <form onSubmit={resend} className="space-y-4" data-testid="setpw-resend-form">
                  <input data-testid="setpw-resend-email" type="email" required value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)} className="acc-input" placeholder="you@practice.co" />
                  <button type="submit" data-testid="setpw-resend-btn"
                    className="lottie-btn w-full text-white font-semibold py-3 rounded-xl transition-colors duration-200">
                    Send me a new link
                  </button>
                </form>
              )}
              <div className="mt-6 text-sm text-navy/70">
                Already set up? <Link to="/login" className="font-semibold text-navy hover:underline" data-testid="setpw-signin-link">Sign in</Link>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`.acc-input{width:100%;background:#fff;border:1px solid #e2e8f0;border-radius:10px;padding:.7rem .85rem;font-size:.9rem;color:#0E0F3F;outline:none;transition:all .15s}.acc-input:focus{border-color:#F37A24;box-shadow:0 0 0 2px rgba(242,111,33,.25)}.lottie-btn{background:#F37A24}.lottie-btn:hover{background:#0E0F3F}`}</style>
    </div>
  );
}
